import React, { useCallback, useEffect, useRef } from "react";

export interface UseBlurBasedFormOptions<T extends Record<string, unknown>> {
  /** Ref for the form container. Clicks outside trigger blur-then-save or full save. */
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Performs the persistence (e.g. API call). Same role as React Query's mutationFn / useFormUtils' mutationFn.
   * Called with partial overrides on field save, or with no args on click-outside (persist current state).
   * Receives Partial<T>, not full T.
   */
  mutationFn: (overrides?: Partial<T>) => void | Promise<void>;
  /** When false, click-outside does not call mutationFn() (e.g. wait until hydrated from server). */
  isSaveReady?: () => boolean;
  /** Current form state; used to snapshot before optimistic update for rollback on error. */
  state: T;
  /** Setters for each field so the hook can apply optimistic updates before calling mutationFn. */
  setters: {
    [K in keyof T]: React.Dispatch<React.SetStateAction<T[K]>>;
  };
  /** Normalize raw values (e.g. from InlineFormField) into form state shape per key. */
  normalizeValue: <K extends keyof T>(key: K, value: unknown) => T[K];
  /** Called when mutationFn resolves (success). For field save, receives the overrides that were persisted. */
  onSuccess?: (overrides?: Partial<T>) => void;
  /** Called when mutationFn rejects (after rollback for field save). Use for toast/logging; rollback still reverts UI. */
  onError?: (error: unknown, overrides?: Partial<T>) => void;
}

export interface BlurBasedFormHandleFieldSaveResult {
  success: boolean;
  error?: string;
}

/**
 * Hook for blur-based / inline-edit forms. Provides:
 * - **handleFieldSave**: pass as `onSave` to InlineTextField, InlineTextAreaField, InlineSelectField,
 *   InlineRadioField, InlineCheckboxListField, and InlineMultiSelectField. Each field saves on blur
 *   (or on change for checkbox/radio/select). Updates are optimistic; on save failure, each key is
 *   rolled back only if its current value still matches this save's optimistic value (so a later
 *   successful edit is never reverted, and an older failed save still restores when a newer save also failed).
 *   Optional onSuccess/onError are for side effects (e.g. refetch, toast); rollback is still required
 *   so the UI reverts to the previous value when the backend fails.
 * - **Click-outside autosave**: clicking outside blurs the focused field (triggering its save) or, if
 *   focus is already outside, calls mutationFn() with no args. isSaveReady() guards when that is allowed.
 *
 * Parent owns state, setters, normalizeValue, and mutationFn (e.g. buildPayload + API call).
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null);
 * const hasHydratedRef = useRef(false);
 * const [familyLife, setFamilyLife] = useState<MultiSelectItem[]>([]);
 * const [socialDesires, setSocialDesires] = useState("");
 * // ... buildPartialPayload, buildFullPayload, mutationFn, setters, normalizeValue, state
 *
 * const { handleFieldSave } = useBlurBasedForm({
 *   containerRef,
 *   mutationFn: savePrioritize,
 *   isSaveReady: () => hasHydratedRef.current,
 *   state,
 *   setters,
 *   normalizeValue,
 * });
 *
 * <div ref={containerRef}>
 *   <InlineMultiSelectField value={familyLife} fieldKey="familyLife" onSave={handleFieldSave} ... />
 *   <InlineTextAreaField value={socialDesires} fieldKey="socialDesires" onSave={handleFieldSave} ... />
 * </div>
 */
export function useBlurBasedForm<T extends Record<string, unknown>>({
  containerRef,
  mutationFn,
  isSaveReady = () => true,
  state,
  setters,
  normalizeValue,
  onSuccess,
  onError,
}: UseBlurBasedFormOptions<T>) {
  // Refs (not state) so the click-outside listener can read the latest values without re-registering.
  // The listener is attached once (effect deps = [containerRef]); if we used state, the handler would
  // close over stale props. We update ref.current each render so the handler always sees the latest
  // mutationFn, isSaveReady, state, etc. isDirtyRef is updated in async callbacks (.then() after save);
  // using state there would trigger re-renders with no UI need.
  const mutateFnRef = useRef(mutationFn);
  const isSaveReadyRef = useRef(isSaveReady);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const stateRef = useRef(state);

  const isDirtyRef = useRef(false);

  mutateFnRef.current = mutationFn;
  isSaveReadyRef.current = isSaveReady;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  stateRef.current = state;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || containerRef.current.contains(event.target as Node)) return;

      const active = document.activeElement as HTMLElement | null;
      const focusWasInside = active && containerRef.current.contains(active);
      if (focusWasInside && active?.blur) {
        active.blur();
        return;
      }
      if (!isSaveReadyRef.current() || !isDirtyRef.current) return;
      void Promise.resolve(mutateFnRef.current())
        .then(() => {
          isDirtyRef.current = false;
          onSuccessRef.current?.();
        })
        .catch((err) => onErrorRef.current?.(err));
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [containerRef]);

  const snapshotState = useCallback((st: T, keys: (keyof T)[]): Partial<T> => {
    const snap: Partial<T> = {};
    keys.forEach((k) => {
      snap[k] = st[k];
    });
    return snap;
  }, []);

  const handleFieldSave = useCallback(
    async (partial: Partial<Record<keyof T, unknown>>): Promise<BlurBasedFormHandleFieldSaveResult> => {
      const overrides: Partial<T> = {};
      const keys = Object.keys(partial) as (keyof T)[];

      // 🔒 Snapshot BEFORE changes (for rollback on error); use ref for latest state at call time
      const previousValues = snapshotState(stateRef.current, keys);

      try {
        // 1️⃣ Optimistic UI update
        for (const key of keys) {
          const setter = setters[key];
          if (!setter) continue;

          const nextValue = normalizeValue(key, partial[key]);
          setter(nextValue);
          overrides[key] = nextValue;
          if (previousValues[key] !== nextValue) {
            isDirtyRef.current = true;
          }
        }

        // 2️⃣ Persist to backend
        await mutationFn(overrides);

        isDirtyRef.current = false;
        onSuccess?.(overrides);
        return { success: true };
      } catch (err) {
        // 🔄 Rollback only keys that still have our optimistic value (value-based, not version-based).
        // Example:
        // Initial value: "A"
        // Save #1: "A" → "B" (optimistic)
        // Save #2: "B" → "C" (optimistic)
        //
        // Case 1: Save #1 ❌ fails AFTER Save #2 ✅ succeeds
        //   currentState = "C", optimistic = "B" → NOT equal → do NOT rollback
        //
        // Case 2: Save #2 ❌ fails
        //   currentState = "C", optimistic = "C" → equal → rollback to "B"
        //
        // Case 3: Save #1 ❌ fails AFTER Save #2 ❌ fails
        //   currentState = "B", optimistic = "B" → equal → rollback to "A"
        //
        // This prevents reverting newer successful edits and ensures failed optimistic
        // state is always cleaned up (even when multiple saves fail).

        const currentState = stateRef.current;
        for (const key of Object.keys(previousValues) as (keyof T)[]) {
          const setter = setters[key];
          if (!setter) continue;
          const valueNow = currentState[key];
          const optimisticValue = overrides[key];
          if (valueNow === optimisticValue) {
            setter(previousValues[key] as T[keyof T]);
          }
        }

        onError?.(err, overrides);
        const message = err instanceof Error ? err.message : "Failed to save changes";

        return { success: false, error: message };
      }
    },
    [mutationFn, setters, normalizeValue, snapshotState, onSuccess, onError],
  );

  return {
    handleFieldSave,
  };
}
